<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Media Library
      </h1>
      <nav class="flex flex-0">
        <FormButton @click="() => {open = !open}" color="blue" prefix-class="fak" icon="fa-plus text-lg">Upload Images</FormButton>
      </nav>
    </header>
    <MediaBrowser class="flex-1 w-full" ref="mediaBrowser" :media="media" :pagination="pagination" :communities="communities" :floorplans="floorplans" :keywords="keywords" @close="media => {this.media = media}" @edit="handleEdit" @page-change="handlePageChange" @limit-change="handleLimitChange" @filter-change="handleFilterChange" @sort-change="handleSortChange" />
    <AttachmentsUpload :open="open" :models="editItems" @close="close" @save="refresh" />
  </div>
</template>

<script>
import axios from "axios";
import LoadingView from "@/components/LoadingView.vue";
import MediaBrowser, { mediaBrowserColumns } from "@/components/MediaBrowser.vue";
import AttachmentsUpload from '@/components/AttachmentsUpload.vue';
import { GlobalPolicy } from "@/policies/global";
import { FormButton } from "@/components/form";
import { userMediaStore } from '@/stores/media';
import { useAuthStore } from '@/stores/auth';

export default {
  setup() {
      return {
          GlobalPolicy
      }
  },
  emits: ['close'],
  data() {
    return {
      isLoading: true,
      communities: [],
      floorplans: [],
      keywords: [],
      media: [],
      pagination: {
        start: 0,
        limit: 24,
        count: 0
      },
      currentSort: {
        column: null,
        direction: null
      },
      open: false,
      editItems: null
    };
  },
  methods: {
    getStoredFilters() {
      const store = userMediaStore();
      const query = store.query;
      return {
        keywords: query.keywords,
        category: query.category.length > 0 ? query.category[0] : null,
        position: query.position.length > 0 ? query.position[0] : null,
        communities: query.communities,
        collections: query.collections,
        floorplans: query.floorplans,
        attached: query.attached,
        search: query.search.string,
      };
    },
    handleEdit(items) {
      this.editItems = items;
      this.open = true;
    },
    isSelected(item) {
        return this.selected.some(i => i.id === item.id);
    },
    index(start = 0, append = false, filters = {}) {
      const params = new URLSearchParams({
        start: start,
        limit: this.pagination.limit,
        ...(filters.category && { 'category': filters.category }),
        ...(filters.position && { 'position': filters.position }),
        ...(filters.attached && { 'attached': filters.attached }),
        ...(filters.search && { 'search': filters.search }),
        ...(this.currentSort.column && { 'sort_by': this.currentSort.column }),
        ...(this.currentSort.direction && { 'sort_dir': this.currentSort.direction }),
      });

      // Handle array parameters properly
      if (filters.keywords?.length) {
        filters.keywords.forEach(k => params.append('keywords[]', k));
      }
      if (filters.communities?.length) {
        filters.communities.forEach(c => params.append('communities[]', c));
      }
      if (filters.collections?.length) {
        filters.collections.forEach(c => params.append('collections[]', c));
      }
      if (filters.floorplans?.length) {
        filters.floorplans.forEach(f => params.append('floorplans[]', f));
      }

      axios
      .get(`media?${params.toString()}`)
      .then((res) => {
        // Handle both old array format and new paginated format
        if (Array.isArray(res.data)) {
          this.media = res.data;
        } else {
          if (append) {
            this.media = [...this.media, ...(res.data.data ?? [])];
          } else {
            this.media = res.data.data ?? [];
          }
          this.pagination = res.data.pagination ?? this.pagination;
        }

        // Only load communities/floorplans/keywords on initial load
        if (start === 0 && this.communities.length === 0) {
          this.indexCommunities();
        } else {
          this.isLoading = false;
        }
      })
      .catch((e) => {
        this.isLoading = false;
        throw e;
      });
    },
    handlePageChange(page, filters) {
      this.isLoading = true;
      const start = (page - 1) * this.pagination.limit;
      // Sync page to store
      const store = userMediaStore();
      store.setPage(page);
      this.index(start, false, filters);
    },
    handleLimitChange(newLimit, filters) {
      this.isLoading = true;
      this.pagination.limit = newLimit;
      // Sync limit to store and reset page
      const store = userMediaStore();
      store.setLimit(newLimit);
      store.setPage(1);
      // Reset to first page when limit changes
      this.index(0, false, filters);
    },
    handleFilterChange(filters) {
      this.isLoading = true;
      // Reset page in store when filters change
      const store = userMediaStore();
      store.setPage(1);
      // Reset to first page when filters change
      this.index(0, false, filters);
    },
    handleSortChange(column, direction, filters) {
      this.isLoading = true;
      this.currentSort.column = column;
      this.currentSort.direction = direction;
      // Reset page in store when sort changes
      const store = userMediaStore();
      store.setPage(1);
      // Reset to first page when sort changes
      this.index(0, false, filters);
    },
    indexCommunities() {
      axios
      .get(`divisions/communities`)
      .then((res) => {
        this.communities = res.data
          .map(community => {
            community.collections = community.collections
              .filter(collection => collection.status != 'inactive')
              .sort((a,b) => a.meta.info.name > b.meta.info.name ? 1 : -1);
            return community;
          })
          .filter(community => community.status != 'inactive' && community.collections.length > 0)
          .sort((a,b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
          this.indexFloorplans();
      })
      .catch((e) => {
        this.isLoading = false;
        throw e;
      });
    },
    indexFloorplans() {
      axios
      .get(`floorplans`)
      .then((res) => {
        this.floorplans = res.data
        this.indexKeywords();
      })
      .catch((e) => {
        this.isLoading = false;
        throw e;
      });
    },
    indexKeywords() {
      axios
      .get(`keywords`)
      .then((res) => {
        this.keywords = res.data
        this.isLoading = false;
      })
      .catch((e) => {
        this.isLoading = false;
        throw e;
      });
    },
    close(clearSelection = false) {
      this.open = false;
      this.editItems = null;
      if (clearSelection && this.$refs.mediaBrowser) {
        this.$refs.mediaBrowser.selected = [];
      }
    },
    refresh() {
      // Preserve filters when refreshing
      this.index(0, false, this.getStoredFilters());
      this.close(true);
    }
  },
  created() {
    // Load stored sort from TablePreferences, falling back if the persisted
    // column was removed or made non-sortable
    const authStore = useAuthStore();
    const tablePrefs = authStore.getTablePrefs('media-list-view', {});
    if (tablePrefs.sort?.column) {
      const sortColumn = mediaBrowserColumns.find(c => c.name === tablePrefs.sort.column);
      if (sortColumn && sortColumn.sort) {
        this.currentSort.column = tablePrefs.sort.column;
        this.currentSort.direction = tablePrefs.sort.dir || 'asc';
      } else {
        const firstSortable = mediaBrowserColumns.find(c => c.sort);
        this.currentSort.column = firstSortable?.name ?? null;
        this.currentSort.direction = firstSortable ? (tablePrefs.sort.dir || 'asc') : null;
      }
    }
    // Load stored pagination from userMediaStore
    const store = userMediaStore();
    this.pagination.limit = store.limit;
    const start = (store.page - 1) * store.limit;
    // Apply stored filters, pagination and sort on initial load
    this.index(start, false, this.getStoredFilters());
  },
  components: {
    LoadingView,
    MediaBrowser,
    AttachmentsUpload,
    FormButton
  },
};
</script>
